import config from '@/config/config';
import { get } from '@/helpers/api.helper';
import type { MaterialInfo } from '@/constants/materialTypeConfig';
import type { VendorRoleType } from '@/constants/vendorRole';

const { matching_api } = config.api;

export const searchVendorProfile = async (targetId: string): Promise<Vendor[]> => {
    const query = `filter=id%7C%7C%24contL%7C%7C${targetId || ''}`;
    const { data } = await get(`${matching_api}/common/search/vendors?${query}`);
    return data;
};

export const getVendorByPath = async ({ vendorPath }: { vendorPath: string }) => {
    // 경량 API 사용 (필요한 필드만 조회)
    const { data } = await get(`${matching_api}/common/search/vendors/by-path/${vendorPath}`);
    // 단일 객체를 배열로 반환 (기존 호환성 유지)
    return data ? [data as Vendor] : [];
};

export const getFilterOptionImages = async ({ mallProductGroupId }: { mallProductGroupId: string }) => {
    const { data } = await get(`${matching_api}/filters/options/images/mallProductGroupId/${mallProductGroupId}`);
    return data as OptionImage[];
};

// 상품 리스트 조회
export const getProductList = async ({ page, limit, filter }: { page: number; limit: number; filter: string }) => {
    const { data } = await get(`${matching_api}/web-vendor/products2?${filter}`, {
        params: {
            page,
            limit,
        },
    });
    return data as { data: Product[]; count: number; page: number; pageCount: number; total: number };
};

export const getProductDetail = async ({ productGroupId }: { productGroupId: string }) => {
    const { data } = await get(`${matching_api}/web-vendor/products2/${productGroupId}`);
    return data as Product;
};

export const getCategories = async (type?: 'MAIN' | 'SUB'): Promise<Category[]> => {
    const categoryType = type || 'MAIN';
    try {
        return await get(`${matching_api}/common/category?type=${categoryType}`).then((r) => {
            return r.data.items;
        });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

// 트리 구조 카테고리 타입 (VM과 동일)
export type CategoryTree = {
    id: string;
    name: string;
    order: number;
    imageUrl: string | null;
    parentCategoryId: string;
    categories?: CategoryTree[];
};

// path가 추가된 카테고리 타입
export type CategoryTreeWithPath = CategoryTree & {
    path: string;
    categories?: CategoryTreeWithPath[];
};

// 트리 구조 카테고리 조회 (VM의 /common/categories 엔드포인트)
export const getCategoryTree = async (): Promise<CategoryTree> => {
    const { data } = await get(`${matching_api}/common/categories?limit=1`);
    return data[0] as CategoryTree;
};

export type Vendor = {
    id: string;
    nickname: string;
    storeName?: string | null;
    email: string;
    imageUrl: string;
    vendorPath: string;
    description: string;
    shop3Banner?: { image: string; url?: string }[];

    // 역할 정보 (신규)
    role?: VendorRoleType; // '도매' | '소매'
    roleId?: string; // vendor_user.role_id

    vendorBusinessInfo: {
        id: string;
        operatorType: string;
        taxationType: null;
        businessType: string;
        businessItem: string;
        settleBank: string;
        settleAccountNumber: string;
        settleBankBookUrl: string;
        settleAccountOwnerName: string;
        businessRegistrationNumber: string;
        businessRegistrationCertificateUrl: string;
        mailOrderSalesRegistrationNumber: string;
        mailOrderSalesRegistrationCertificateUrl: string;
        additionalCertificateUrl: any[];
        interestCategories: string[];
        ownerName: string;
        companyName: string;
        companyAddress: string;
        managerPhoneNumber: string;
        customerCenterNumber: string;
        termsAgreedAt: null;
        updatedAt: string;
        createdAt: string;
        shippingReturnAddress: {
            postCode: string;
            recipientAddress: string;
            recipientAddressDetail: string;
        };
        managerName: string;
        verifyBusinessInfo: 'f' | 't' | null;
        verifyAccountInfo: 'f' | 't' | null;
        storeLocation: string | null;
        storeName: string | null;
        snsInfo: {
            platform: string;
            url: string;
            name: string;
        }[];
    };
};

export type Category = {
    categoryId: string;
    categoryName: string;
    imageUrl: string;
    parentCategoryId: string;
    sortOrder: number;
    wholeCategoryId: string;
    wholeCategoryName: string;
};

export type Product = {
    id: string;
    vendorId: string;
    representProductId: string;
    categoryId: string;
    groupTitle: string;
    representImageUrl: string;
    tags: [];
    description: string;
    salesPolicyDescription: string;
    createdAt: string;
    groupImageUrl: string[];
    // 제품 설명 섹션에 대표/그룹 이미지 노출 여부 및 위치
    useThumbnailInDetail?: 't' | 'f' | null;
    thumbnailInDetailPosition?: 'top' | 'bottom' | null;
    optionShowOrder: string[];
    productInfo: {
        product_id: string;
        options: {
            optionKey?: string;
            option_key?: string;
            optionValue?: string;
            option_value?: string;
        }[];
    }[];
    productDocuments: [
        {
            list: string[];
            name: string;
            listId: string;
        },
    ];
    isPublic: string;
    shippingReturnAddressBookId: string;
    onlineMinPriceLink: string;
    isPrimaryProduct: string;
    followerCutoffMin: number;
    manufacturer: string;
    countryOfOrigin: string;
    manufactureDate: string;
    afterServiceManager: string;
    afterServicePhone: string;
    categoryTags: string[];
    productCode: string | null;
    isSetProduct: string;
    setSelectionCount: number;
    optionSelectorType: string;
    fabricInfo: {
        lining: string;
        thickness: string;
        elasticity: string;
        seeThrough: string;
    };
    washInfo: {
        dry: string;
        iron: string;
        bleach: string;
        method: string;
        special: string;
        etc: string[];
    };
    materialInfo: MaterialInfo[] | null;
    colorNameInfo: {
        [key: string]: string;
    };
    sizeInfo: { size: string; [key: string]: string }[];
    sizeType: string;
    optionInputType: string;
    matchingProducts: ProductOption[];
    matchingSponsorshipCondition: {
        id: string;
        conditionType: string | null;
        condition: string;
        managerName: string;
        managerPhone: string;
        managerEmail: string;
        matchingProductGroupId: string;
        createdAt: string;
        shippingReturnAddress: {
            recipientAddressDetail: string;
        };
    };
    vendor: {
        id: string;
        nickname: string;
        storeName?: string | null;
        imageUrl: string;
        vendorBusinessInfo?: {
            storeLocation: string | null;
            storeName: string | null;
        };
    };
    singleUnitAvailable?: 't' | 'f' | null;
    isSampleRequestAvailable?: 't' | 'f' | null;
};

export type ProductOption = {
    id: string;
    optionName: string;
    inventory: number;
    supplyPrice: number;
    marketPrice: number;
    discountMethod: string;
    discountValue: number;
    productCharge: number;
    isPublic: string;
    createdAt: string;
    isActivated: string;
    marginRate: number;
    productCode: string | null;
    optionImageUrl: string | null;
    color: string | null;
};

export type OptionImage = {
    optionBaseImageId: string;
    optionBaseImageUrl: string;
    options: Option[];
};

export type Option = {
    optionId: string;
    optionImageId: string;
    optionValue: string;
    optionDescription: string;
    optionImageUrl: string;
};

export type WashInfo = {
    dry: string;
    iron: string;
    bleach: string;
    method: string;
    special: string;
    etc: string[];
};

export type DimEntity = {
    id: string;
    name: string;
    sortOrder: number;
};

export type DimEntitiesResponse = {
    dimFit: DimEntity[];
    dimPattern: DimEntity[];
    dimSeason: DimEntity[];
    dimSheerness: DimEntity[];
    dimStretch: DimEntity[];
    dimStyle: DimEntity[];
    dimThickness: DimEntity[];
    dimLining?: DimEntity[];
    dimDetail: DimEntity[];
};

// 상품 리스트 전용 간소화 타입
export type VendorShopProduct = {
    id: string;
    groupTitle: string;
    representImageUrl: string;
    representImageUrlSizes: { origin: string; small: string; medium: string; large: string };
    vendorId: string;
    createdAt: string;
    supplyPrice: number;
    marketPrice: number;
    discountMethod: string;
    discountValue: number;
    vendorNickname: string;
    totalInventory?: number;
};

export type VendorShopProductListResponse = {
    data: VendorShopProduct[];
    page: number;
    pageCount: number;
    total: number;
};

export const getVendorShopProducts = async (params: {
    vendorId: string;
    keyword?: string;
    tab?: string;
    minPrice?: number;
    maxPrice?: number;
    page?: number;
    limit?: number;
}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    const { data } = await get(`${matching_api}/web-vendor/products2/vendor-shop-list?${query.toString()}`);
    return data as VendorShopProductListResponse;
};

export const getDimEntities = async (): Promise<DimEntitiesResponse> => {
    const { data } = await get(`${matching_api}/web-vendor/products/dim-entities`);
    return data as DimEntitiesResponse;
};
